import {
  AlertTriangle,
  PhoneCall,
  ShieldAlert,
  Ban,
  ArrowLeftRight,
  Camera,
  Users,
  ArrowRight,
  Phone,
} from "lucide-react";
import Seo from "@/components/Seo";

const CHECKLIST = [
  {
    icon: AlertTriangle,
    title: "Make sure everyone is safe",
    text: "Check on everyone involved. If anyone is injured, call 000 immediately.",
  },
  {
    icon: ShieldAlert,
    title: "Call the police if required",
    text: "Police attendance is required in some situations – for example injuries, or a driver who refuses to exchange details.",
  },
  {
    icon: Ban,
    title: "Don't admit fault",
    text: "Stay calm and polite, but don't apologise or accept blame at the scene – that's for the insurers to work out.",
  },
  {
    icon: ArrowLeftRight,
    title: "Exchange details with the other driver",
    text: "Get their name, phone number, address, licence number, registration number and insurer – and give them yours.",
  },
  {
    icon: Camera,
    title: "Take photos",
    text: "Photograph both vehicles, the number plates, the accident scene and the other driver's licence.",
  },
  {
    icon: Users,
    title: "Get witness details",
    text: "If anyone saw what happened, ask for their name and phone number.",
  },
  {
    icon: PhoneCall,
    title: "Then call us or start your claim online",
    text: "Call 1800 957 474 or start your claim on your phone – we'll take it from there.",
  },
];

export default function AccidentHelp() {
  return (
    <>
      <Seo
        title="Been in an accident? What to do | Reliance Motor Claims Recovery"
        description="A simple checklist for what to do after a car accident: stay safe, exchange details, take photos, then call 1800 957 474 or start your claim online."
        path="/accident-help"
      />

      <section className="bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-20">
          <p className="font-saira font-semibold text-xs sm:text-sm tracking-widest uppercase text-steel border-l-4 border-road pl-3">
            Accident? What to do
          </p>
          <h1 className="mt-4 text-4xl sm:text-5xl font-bold tracking-tight text-ink max-w-3xl">
            Just had an accident? Work through this list.
          </h1>
          <p className="mt-6 text-base sm:text-lg leading-relaxed text-steel max-w-2xl">
            Accidents are stressful. This checklist covers what matters at the scene – once
            you're safe, hand the rest to us.
          </p>
        </div>
      </section>

      <section className="bg-mist">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-20">
          <ol className="space-y-4">
            {CHECKLIST.map((item, i) => (
              <li
                key={item.title}
                className="bg-white rounded-lg p-5 sm:p-7 flex gap-4 sm:gap-6 items-start border border-transparent hover:border-road/40 transition-colors"
                data-testid={`checklist-item-${i + 1}`}
              >
                <div className="flex flex-col items-center gap-2 shrink-0">
                  <span className="inline-flex items-center justify-center size-12 sm:size-14 bg-mist rounded-lg">
                    <item.icon className="size-6 sm:size-7 text-ink" strokeWidth={1.75} aria-hidden="true" />
                  </span>
                  <span className="font-saira font-bold text-road text-lg" aria-hidden="true">
                    {i + 1}
                  </span>
                </div>
                <div>
                  <h2 className="font-saira font-semibold text-lg sm:text-xl text-ink">{item.title}</h2>
                  <p className="mt-2 text-sm sm:text-base text-steel leading-relaxed">{item.text}</p>
                </div>
              </li>
            ))}
          </ol>

          <div className="mt-12 bg-ink text-white rounded-2xl px-6 py-10 sm:px-10 border-l-8 border-road">
            <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight">
              Safe and sorted? Start your claim now.
            </h2>
            <p className="mt-3 text-white/75 text-base sm:text-lg leading-relaxed max-w-2xl">
              If the accident wasn't your fault, we'll manage your claim, replacement hire
              vehicle, storage and repairs, and claim the costs from the at-fault party's insurer.
            </p>
            <div className="mt-7 flex flex-col sm:flex-row gap-4">
              <a
                href="/claim"
                className="inline-flex items-center justify-center gap-2 min-h-[52px] px-8 bg-white text-ink font-saira font-bold rounded hover:bg-mist transition-colors"
                data-testid="accident-help-start-claim-button"
              >
                Start your claim
                <ArrowRight className="size-5" aria-hidden="true" />
              </a>
              <a
                href="tel:1800957474"
                className="inline-flex items-center justify-center gap-2 min-h-[52px] px-8 bg-transparent text-white font-saira font-bold rounded border border-white/30 hover:bg-white/10 transition-colors"
                data-testid="accident-help-call-button"
              >
                <Phone className="size-5" aria-hidden="true" />
                Call 1800 957 474
              </a>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
